// src/pages/AccountPage.jsx

import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FaUserCircle,
  FaCog,
  FaTimes,
  FaLock,
  FaSpinner,
  FaArrowLeft,
  FaCheckCircle,
  FaExclamationTriangle,
  FaEye,
  FaEyeSlash,
  FaKey,
  FaUser,
  FaEnvelope,
  FaBox,
  FaShoppingCart,
  FaRobot,
  FaChartLine,
  FaExternalLinkAlt,
  FaBolt,
  FaShieldAlt,
  FaTicketAlt,
  FaCopy
} from "react-icons/fa";

import "../styles/AccountPage.css";

export default function AccountPage() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [activeTab, setActiveTab] = useState("profile");
  const [isMobile, setIsMobile] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const [myOrders, setMyOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [ordersError, setOrdersError] = useState("");
  const [myDiscounts, setMyDiscounts] = useState([]);
  const [loadingDiscounts, setLoadingDiscounts] = useState(false);
  const [discountsError, setDiscountsError] = useState("");
  const [revealedDiscounts, setRevealedDiscounts] = useState({});
  const [copiedDiscountId, setCopiedDiscountId] = useState(null);

  const [paymentVerifying, setPaymentVerifying] = useState(false);
  const [paymentNotice, setPaymentNotice] = useState("");
  const [paymentError, setPaymentError] = useState("");

  const [settings, setSettings] = useState({
    name: "",
    email: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState("");
  const [settingsSuccess, setSettingsSuccess] = useState("");
  const [nameUpdateSuccess, setNameUpdateSuccess] = useState(false);
  const [passwordUpdateSuccess, setPasswordUpdateSuccess] = useState(false);

  const [showVerificationScreen, setShowVerificationScreen] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const [showSettingsModal, setShowSettingsModal] = useState(false);

  const navigate = useNavigate();
  const paymentCheckDoneRef = useRef(false);

  const API_BASE = "https://reanimeitor.com/backend";
  const CHATBOT_URL = "https://reanimeitor.com/blog/reanimeitor-ia/";

  const isSysAdmin = (userData) => {
    return String(userData?.role || "").trim() === "sys_admin";
  };

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

   useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");

    if (tab === "orders" || tab === "assistant" || tab === "profile" || tab === "discounts") {
      setActiveTab(tab);
    }
  }, []);
  const fetchMyDiscounts = async () => {
    setLoadingDiscounts(true);
    setDiscountsError("");

    try {
      const res = await axios.get(`${API_BASE}/get_my_discounts.php`, {
        withCredentials: true,
        timeout: 10000
      });

      if (res.data.success) {
        setMyDiscounts(res.data.discounts || []);
      } else {
        setDiscountsError(res.data.error || "Error al cargar descuentos");
      }
    } catch (err) {
      console.error("Error cargando descuentos:", err);
      setDiscountsError("Error de conexión al cargar descuentos");
    } finally {
      setLoadingDiscounts(false);
    }
  };
  const fetchMyOrders = async () => {
    setLoadingOrders(true);
    setOrdersError("");

    try {
      const res = await axios.get(`${API_BASE}/get_user_orders.php`, {
        withCredentials: true,
        timeout: 10000
      });

      if (res.data.success) {
        setMyOrders(res.data.orders || []);
      } else {
        setOrdersError(res.data.error || "Error al cargar pedidos");
      }
    } catch (err) {
      console.error("Error cargando pedidos:", err);
      setOrdersError("Error de conexión al cargar pedidos");
    } finally {
      setLoadingOrders(false);
    }
  };

  const verifyPaymentFromUrl = async () => {
    if (paymentCheckDoneRef.current) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const success = params.get("pack_success");
    const sessionId = params.get("session_id");

    if (success !== "true" || !sessionId) {
      return;
    }

    paymentCheckDoneRef.current = true;
    setPaymentVerifying(true);
    setPaymentNotice("");
    setPaymentError("");
    setActiveTab("orders");

    try {
      const res = await axios.post(
        `${API_BASE}/verify_payment.php`,
        {
          session_id: sessionId
        },
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json"
          },
          timeout: 20000
        }
      );

      if (res.data?.success && res.data?.paid) {
        setPaymentNotice(
          `Pedido confirmado correctamente${res.data.order_number ? `: ${res.data.order_number}` : ""}.`
        );

        await fetchMyOrders();

        window.history.replaceState(
          {},
          document.title,
          `${window.location.pathname}?tab=orders`
        );
      } else if (res.data?.pending) {
        setPaymentError("El pago todavía aparece pendiente. Si se ha cobrado, actualiza en unos segundos.");
      } else {
        setPaymentError(res.data?.error || "No se pudo confirmar el pago.");
      }
    } catch (err) {
      console.error("Error verificando pago:", err);
      setPaymentError("Error de conexión verificando el pago.");
    } finally {
      setPaymentVerifying(false);
    }
  };

  const loadUserProfile = async () => {
    try {
      const response = await axios.get(`${API_BASE}/profile.php`, {
        withCredentials: true,
        timeout: 10000
      });

      if (response.data.user) {
        const userData = response.data.user;

        setUser(userData);
        setIsAdmin(isSysAdmin(userData));

        setSettings((prev) => ({
          ...prev,
          name: userData.name || "",
          email: userData.email || ""
        }));

        localStorage.setItem("userData", JSON.stringify(userData));

        return true;
      }

      return false;
    } catch (err) {
      console.error("Error cargando perfil:", err);
      return false;
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      setError("");

      try {
        const res = await axios.get(`${API_BASE}/profile.php`, {
          withCredentials: true,
          timeout: 10000
        });

        if (res.data.user) {
          const userData = res.data.user;

          setUser(userData);
          setIsAdmin(isSysAdmin(userData));

          setSettings((prev) => ({
            ...prev,
            name: userData.name || "",
            email: userData.email || ""
          }));

          localStorage.setItem("userData", JSON.stringify(userData));
        } else {
          localStorage.removeItem("userData");
          navigate("/login");
        }
      } catch (err) {
        console.error("Error cargando usuario desde servidor:", err);

        const cachedData = localStorage.getItem("userData");

        if (cachedData) {
          try {
            const parsed = JSON.parse(cachedData);

            setUser(parsed);
            setIsAdmin(isSysAdmin(parsed));

            setSettings((prev) => ({
              ...prev,
              name: parsed.name || "",
              email: parsed.email || ""
            }));
          } catch (parseError) {
            localStorage.removeItem("userData");
            navigate("/login");
          }
        } else {
          navigate("/login");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [navigate]);

   useEffect(() => {
    if (activeTab === "orders" && user?.id) {
      fetchMyOrders();
    }

    if (activeTab === "discounts" && user?.id) {
      fetchMyDiscounts();
    }
  }, [activeTab, user?.id]);

  useEffect(() => {
    if (user?.id) {
      verifyPaymentFromUrl();
    }
  }, [user?.id]);
  const handleRevealDiscount = (discountId) => {
    setRevealedDiscounts((prev) => ({
      ...prev,
      [discountId]: true
    }));
  };

  const handleCopyDiscount = async (discount) => {
    if (!discount?.code) {
      return;
    }

    try {
      await navigator.clipboard.writeText(discount.code);
      setCopiedDiscountId(discount.id);

      setTimeout(() => {
        setCopiedDiscountId(null);
      }, 1800);
    } catch (err) {
      console.error("No se pudo copiar el descuento:", err);
    }
  };
  const handleSettingsChange = (e) => {
    const { name, value } = e.target;

    setSettings((prev) => ({
      ...prev,
      [name]: value
    }));

    setSettingsError("");
    setSettingsSuccess("");
  };

  const handleSendVerificationCode = async () => {
    setVerificationLoading(true);
    setSettingsError("");

    try {
      const response = await axios.post(
        `${API_BASE}/actualizar_perfil.php`,
        { accion: "enviar_codigo" },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
          timeout: 10000
        }
      );

      if (response.data.success) {
        setShowVerificationScreen(true);
      } else {
        setSettingsError(response.data.error || "Error al enviar código");
      }
    } catch (err) {
      setSettingsError("Error de conexión");
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) {
      setSettingsError("El código debe tener 6 dígitos");
      return;
    }

    setVerificationLoading(true);
    setSettingsError("");

    try {
      const response = await axios.post(
        `${API_BASE}/actualizar_perfil.php`,
        { accion: "verificar", codigo: verificationCode },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
          timeout: 10000
        }
      );

      if (response.data.success) {
        setIsVerified(true);
        setShowVerificationScreen(false);
        setVerificationCode("");
        setSettingsSuccess("Identidad verificada. Ya puedes realizar cambios.");

        setTimeout(() => setSettingsSuccess(""), 3000);
      } else {
        setSettingsError(response.data.error || "Código incorrecto");
      }
    } catch (err) {
      setSettingsError("Error de conexión");
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendDisabled(true);
    setResendTimer(60);

    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setResendDisabled(false);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    try {
      await axios.post(
        `${API_BASE}/actualizar_perfil.php`,
        { accion: "enviar_codigo" },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
          timeout: 10000
        }
      );
    } catch (err) {
      console.error("Error reenviando código:", err);
    }
  };

  const handleUpdateName = async () => {
    if (!settings.name.trim()) {
      setSettingsError("El nombre no puede estar vacío");
      return;
    }

    setSettingsLoading(true);
    setSettingsError("");

    try {
      const response = await axios.post(
        `${API_BASE}/actualizar_perfil.php`,
        {
          accion: "cambiar_nombre",
          nombre: settings.name.trim()
        },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
          timeout: 10000
        }
      );

      if (response.data.success) {
        const updatedUser = {
          ...user,
          name: settings.name.trim()
        };

        setUser(updatedUser);
        localStorage.setItem("userData", JSON.stringify(updatedUser));

        setNameUpdateSuccess(true);
        setSettingsSuccess("Nombre actualizado correctamente");

        await loadUserProfile();

        setTimeout(() => {
          setNameUpdateSuccess(false);
          setSettingsSuccess("");
        }, 3000);
      } else {
        setSettingsError(response.data.error || "Error al actualizar nombre");
      }
    } catch (err) {
      setSettingsError("Error de conexión al actualizar nombre");
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!settings.currentPassword) {
      setSettingsError("La contraseña actual es requerida");
      return;
    }

    if (!settings.newPassword || settings.newPassword.length < 8) {
      setSettingsError("La nueva contraseña debe tener al menos 8 caracteres");
      return;
    }

    if (settings.newPassword !== settings.confirmPassword) {
      setSettingsError("Las contraseñas no coinciden");
      return;
    }

    setSettingsLoading(true);
    setSettingsError("");

    try {
      const response = await axios.post(
        `${API_BASE}/actualizar_perfil.php`,
        {
          accion: "cambiar_password",
          password_actual: settings.currentPassword,
          password_nuevo: settings.newPassword
        },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
          timeout: 10000
        }
      );

      if (response.data.success) {
        setPasswordUpdateSuccess(true);
        setSettingsSuccess("Contraseña actualizada correctamente");

        setSettings((prev) => ({
          ...prev,
          currentPassword: "",
          newPassword: "",
          confirmPassword: ""
        }));

        setTimeout(() => {
          setPasswordUpdateSuccess(false);
          setSettingsSuccess("");
        }, 3000);
      } else {
        setSettingsError(response.data.error || "Error al actualizar contraseña");
      }
    } catch (err) {
      setSettingsError("Error de conexión al actualizar contraseña");
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleUpdateNameWithVerification = () => {
    if (!isVerified) {
      setSettingsError("Debes verificar tu identidad primero");
      return;
    }

    handleUpdateName();
  };

  const handleUpdatePasswordWithVerification = () => {
    if (!isVerified) {
      setSettingsError("Debes verificar tu identidad primero");
      return;
    }

    handleUpdatePassword();
  };

  const closeSettingsModal = () => {
    setShowSettingsModal(false);
    setSettingsError("");
    setSettingsSuccess("");
    setShowVerificationScreen(false);
    setVerificationCode("");
  };

  const handleLogout = () => {
    localStorage.removeItem("userData");
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="account-loading">
        <div className="loading-spinner-professional">
          <div className="energy-pulse"></div>
          <div className="loading-text">Cargando cuenta...</div>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="account-error">
        <div className="error-container">
          <div className="error-icon">⚡</div>
          <h3>Error</h3>
          <p>{error || "No se cargaron los datos de la cuenta."}</p>
          <button onClick={() => navigate("/login")} className="energy-btn primary">
            Reconectar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="account-professional">
      <div className="account-energy-field">
        <div className="energy-bolt"></div>
        <div className="energy-bolt"></div>
        <div className="energy-orb energy-orb-one"></div>
        <div className="energy-orb energy-orb-two"></div>
      </div>

      {showSettingsModal && (
        <div className="payment-modal-overlay">
          <div className="payment-modal settings-modal">
            <div className="modal-header">
              <h3>
                <FaCog /> Configuración de cuenta
              </h3>

              <button className="modal-close-btn" onClick={closeSettingsModal}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              {settingsSuccess && (
                <div className="settings-success-message">
                  <FaCheckCircle /> {settingsSuccess}
                </div>
              )}

              {settingsError && (
                <div className="settings-error-message">
                  <FaExclamationTriangle /> {settingsError}
                </div>
              )}

              {showVerificationScreen ? (
                <div className="verification-screen">
                  <div className="verification-icon">
                    <FaLock className="verification-lock-icon" />
                  </div>

                  <h4>Verifica tu identidad</h4>

                  <p className="verification-description">
                    Hemos enviado un código de verificación a:
                    <br />
                    <strong>{user?.email}</strong>
                  </p>

                  <p className="verification-instructions">
                    Revisa tu bandeja de entrada y spam. El código expira en 15 minutos.
                  </p>

                  <div className="verification-code-input">
                    <input
                      type="text"
                      maxLength="6"
                      placeholder="000000"
                      value={verificationCode}
                      onChange={(e) => {
                        setVerificationCode(
                          e.target.value.replace(/[^0-9]/g, "").slice(0, 6)
                        );
                        setSettingsError("");
                      }}
                      className="code-input-field"
                      autoFocus
                      disabled={verificationLoading}
                    />
                  </div>

                  <div className="verification-actions">
                    <button
                      className="verify-btn"
                      onClick={handleVerifyCode}
                      disabled={verificationCode.length !== 6 || verificationLoading}
                    >
                      {verificationLoading ? (
                        <>
                          <FaSpinner className="spinning-icon" /> Verificando...
                        </>
                      ) : (
                        "Verificar código"
                      )}
                    </button>

                    <button
                      className="resend-code-btn"
                      onClick={handleResendCode}
                      disabled={resendDisabled || verificationLoading}
                    >
                      {resendDisabled ? `Reenviar en ${resendTimer}s` : "Reenviar código"}
                    </button>
                  </div>

                  <button
                    className="back-to-settings-btn"
                    onClick={() => {
                      setShowVerificationScreen(false);
                      setVerificationCode("");
                      setSettingsError("");
                    }}
                  >
                    <FaArrowLeft /> Volver a configuración
                  </button>
                </div>
              ) : (
                <>
                  <div className="settings-section">
                    <div className="settings-section-header">
                      <FaUser /> <h4>Información personal</h4>
                    </div>

                    <div className="settings-field-group">
                      <label className="settings-label">
                        <FaEnvelope /> Email
                      </label>
                      <input
                        type="email"
                        value={settings.email}
                        disabled
                        className="settings-input disabled"
                      />
                    </div>

                    <div className="settings-field-group">
                      <label className="settings-label">
                        <FaUser /> Nombre
                      </label>

                      <div className="settings-field-with-action">
                        <input
                          type="text"
                          name="name"
                          value={settings.name}
                          onChange={handleSettingsChange}
                          placeholder="Tu nombre"
                          className="settings-input"
                          disabled={!isVerified}
                        />

                        <button
                          className={`settings-action-btn ${nameUpdateSuccess ? "success" : ""}`}
                          onClick={handleUpdateNameWithVerification}
                          disabled={
                            settingsLoading ||
                            !settings.name.trim() ||
                            settings.name === user.name ||
                            !isVerified
                          }
                        >
                          {settingsLoading ? (
                            <FaSpinner className="spinning-icon" />
                          ) : nameUpdateSuccess ? (
                            <FaCheckCircle />
                          ) : (
                            <FaUser />
                          )}
                          <span>Actualizar</span>
                        </button>
                      </div>

                      {!isVerified && (
                        <p className="verification-warning">
                          <FaExclamationTriangle /> Debes verificar tu identidad para cambiar el nombre.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="settings-section">
                    <div className="settings-section-header">
                      <FaKey /> <h4>Cambiar contraseña</h4>
                    </div>

                    <div className="settings-field-group">
                      <label className="settings-label">
                        <FaLock /> Actual
                      </label>

                      <div className="password-input-wrapper">
                        <input
                          type={showCurrentPassword ? "text" : "password"}
                          name="currentPassword"
                          value={settings.currentPassword}
                          onChange={handleSettingsChange}
                          placeholder="••••••••"
                          className="settings-input"
                          disabled={!isVerified}
                        />

                        <button
                          type="button"
                          className="password-toggle-btn"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        >
                          {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                    </div>

                    <div className="settings-field-group">
                      <label className="settings-label">
                        <FaLock /> Nueva
                      </label>

                      <div className="password-input-wrapper">
                        <input
                          type={showNewPassword ? "text" : "password"}
                          name="newPassword"
                          value={settings.newPassword}
                          onChange={handleSettingsChange}
                          placeholder="Mínimo 8 caracteres"
                          className="settings-input"
                          disabled={!isVerified}
                        />

                        <button
                          type="button"
                          className="password-toggle-btn"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                    </div>

                    <div className="settings-field-group">
                      <label className="settings-label">
                        <FaLock /> Confirmar
                      </label>

                      <div className="password-input-wrapper">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          name="confirmPassword"
                          value={settings.confirmPassword}
                          onChange={handleSettingsChange}
                          placeholder="Repite tu contraseña"
                          className="settings-input"
                          disabled={!isVerified}
                        />

                        <button
                          type="button"
                          className="password-toggle-btn"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                    </div>

                    {!isVerified && (
                      <p className="verification-warning">
                        <FaExclamationTriangle /> Debes verificar tu identidad para cambiar la contraseña.
                      </p>
                    )}

                    <button
                      className={`settings-password-btn ${passwordUpdateSuccess ? "success" : ""}`}
                      onClick={handleUpdatePasswordWithVerification}
                      disabled={
                        settingsLoading ||
                        !settings.currentPassword ||
                        !settings.newPassword ||
                        settings.newPassword.length < 8 ||
                        settings.newPassword !== settings.confirmPassword ||
                        !isVerified
                      }
                    >
                      {settingsLoading ? (
                        <>
                          <FaSpinner className="spinning-icon" /> Actualizando...
                        </>
                      ) : passwordUpdateSuccess ? (
                        <>
                          <FaCheckCircle /> Contraseña actualizada
                        </>
                      ) : (
                        <>
                          <FaKey /> Cambiar contraseña
                        </>
                      )}
                    </button>
                  </div>

                  {!isVerified && (
                    <div className="verify-account-section">
                      <button
                        className="verify-account-btn"
                        onClick={handleSendVerificationCode}
                        disabled={verificationLoading}
                      >
                        {verificationLoading ? (
                          <>
                            <FaSpinner className="spinning-icon" /> Enviando código...
                          </>
                        ) : (
                          <>
                            <FaLock /> Verificar identidad para realizar cambios
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {isVerified && (
                    <div className="verified-badge">
                      <FaCheckCircle /> Identidad verificada. Puedes realizar cambios.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <section className="account-hero">
        <div className="account-hero-copy">
          <div className="account-kicker">
            <FaBolt />
            Área privada Reanimeitor
          </div>

          <h1>Hola, {user.name || "Usuario"}</h1>

          <p>
            Gestiona tu perfil, revisa tus pedidos y habla con el asistente de Reanimeitor desde tu cuenta.
          </p>

          <div className="account-quick-stats">
            <div>
              <strong>{myOrders.length}</strong>
              <span>Pedidos</span>
            </div>

            <div>
              <strong>{isAdmin ? "Admin" : "Cliente"}</strong>
              <span>Rol de cuenta</span>
            </div>

            <div>
              <strong>{isVerified ? "Verificada" : "Pendiente"}</strong>
              <span>Seguridad</span>
            </div>
          </div>
        </div>

        <div className="account-hero-actions">
          <button className="account-pill-btn" onClick={() => setShowSettingsModal(true)}>
            <FaCog /> Configuración
          </button>

          {isAdmin && (
            <a
              href="https://reanimeitor.com/stats"
              target="_blank"
              rel="noopener noreferrer"
              className="account-pill-btn admin-panel-btn"
            >
              <FaChartLine /> Panel
            </a>
          )}

          <button className="account-pill-btn logout-pill-btn" onClick={handleLogout}>
            Salir
          </button>
        </div>
      </section>

      <section className="account-tabs">
        <button
          className={`tab-btn ${activeTab === "profile" ? "active" : ""}`}
          onClick={() => setActiveTab("profile")}
        >
          <FaUserCircle /> Perfil
        </button>

               <button
          className={`tab-btn ${activeTab === "orders" ? "active" : ""}`}
          onClick={() => setActiveTab("orders")}
        >
          <FaBox /> Pedidos
        </button>

        <button
          className={`tab-btn ${activeTab === "discounts" ? "active" : ""}`}
          onClick={() => setActiveTab("discounts")}
        >
          <FaTicketAlt /> Descuentos
        </button>

        <button
          className={`tab-btn ${activeTab === "assistant" ? "active" : ""}`}
          onClick={() => setActiveTab("assistant")}
        >
          <FaRobot /> Chat IA
        </button>
      </section>

      <main className="account-content-professional">
        {activeTab === "profile" && (
          <div className="account-grid-layout">
            <section className="user-info-card account-glass-card">
              <div className="card-title-row">
                <h3>
                  <FaUserCircle /> Información de la cuenta
                </h3>

                <span className={`role-badge ${isAdmin ? "admin" : "client"}`}>
                  {isAdmin ? "sys_admin" : "cliente"}
                </span>
              </div>

              <div className="user-details-grid">
                <div className="detail-item">
                  <span className="label">ID</span>
                  <span className="value">{user.id}</span>
                </div>

                <div className="detail-item">
                  <span className="label">Email</span>
                  <span className="value">{user.email}</span>
                </div>

                <div className="detail-item">
                  <span className="label">Nombre</span>
                  <span className="value">{user.name || "—"}</span>
                </div>

                <div className="detail-item">
                  <span className="label">Miembro desde</span>
                  <span className="value">
                    {new Date(user.created_at || Date.now()).toLocaleDateString("es-ES")}
                  </span>
                </div>
              </div>

              <div className="profile-actions-row">
                <button className="edit-profile-btn" onClick={() => setShowSettingsModal(true)}>
                  <FaCog /> Editar perfil
                </button>

                {isAdmin && (
                  <a
                    href="https://reanimeitor.com/stats"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="edit-profile-btn admin-shortcut"
                  >
                    <FaChartLine /> Panel privado
                  </a>
                )}
              </div>
            </section>

            <section className="account-status-card account-glass-card">
              <h3>
                <FaShieldAlt /> Estado de cuenta
              </h3>

              <div className="status-list">
                <div className="status-row">
                  <span>Sesión activa</span>
                  <strong>Conectado</strong>
                </div>

                <div className="status-row">
                  <span>Dispositivo</span>
                  <strong>{isMobile ? "Móvil" : "Escritorio"}</strong>
                </div>

                <div className="status-row">
                  <span>Acceso panel</span>
                  <strong>{isAdmin ? "Permitido" : "No disponible"}</strong>
                </div>

                <div className="status-row">
                  <span>Verificación</span>
                  <strong>{isVerified ? "Verificada" : "No verificada"}</strong>
                </div>
              </div>
            </section>
          </div>
        )}
        {activeTab === "discounts" && (
          <div className="discounts-tab-content">
            <div className="section-header">
              <h2>
                <FaTicketAlt /> Mis descuentos
              </h2>
              <p>Tus códigos ganados en el Rasca y Gana Reanimeitor.</p>
            </div>

            {loadingDiscounts ? (
              <div className="loading-orders">
                <div className="spinner-large"></div>
                <p>Cargando descuentos...</p>
              </div>
            ) : discountsError ? (
              <div className="orders-error-message">
                <FaExclamationTriangle className="error-icon" />
                <h4>Error</h4>
                <p>{discountsError}</p>
                <button className="retry-btn" onClick={fetchMyDiscounts}>
                  Reintentar
                </button>
              </div>
            ) : myDiscounts.length === 0 ? (
              <div className="empty-orders">
                <FaTicketAlt className="empty-icon-large" />
                <h4>No tienes descuentos</h4>
                <p>Cuando ganes un descuento en el Rasca y Gana aparecerá aquí.</p>
                <button
                  className="action-btn-empty"
                  onClick={() => {
                    window.location.href = "https://reanimeitor.com/blog/acceso-rasca/";
                  }}
                >
                  <FaBolt /> Ir al Rasca y Gana
                </button>
              </div>
            ) : (
              <div className="discounts-grid">
                {myDiscounts.map((discount) => {
                  const isRevealed = !!revealedDiscounts[discount.id];
                  const isUsed = discount.status === "used";

                  return (
                    <div
                      key={discount.id}
                      className={`discount-card ${isUsed ? "used" : "available"}`}
                    >
                      <div className="discount-card-header">
                        <div className="discount-icon-wrap">
                          <FaTicketAlt />
                        </div>

                        <span className={`discount-status ${isUsed ? "used" : "available"}`}>
                          {discount.status_label || (isUsed ? "Usado" : "Por usar")}
                        </span>
                      </div>

                      <div className="discount-main-info">
                        <h3>{discount.discount_percent || 10}% de descuento</h3>
                        <p>Premio conseguido en Reanimeitor.</p>
                      </div>

                      <div className="discount-code-box-account">
                        <span className="discount-code-label">Código</span>

                        <strong className={isRevealed ? "revealed" : "hidden"}>
                          {isRevealed ? discount.code : discount.masked_code || "••••••••••••"}
                        </strong>
                      </div>

                      <div className="discount-card-actions">
                        {!isRevealed ? (
                          <button
                            className="discount-action-btn reveal"
                            onClick={() => handleRevealDiscount(discount.id)}
                          >
                            <FaEye /> Revelar
                          </button>
                        ) : (
                          <button
                            className="discount-action-btn copy"
                            onClick={() => handleCopyDiscount(discount)}
                            disabled={isUsed}
                          >
                            <FaCopy /> {copiedDiscountId === discount.id ? "Copiado" : "Copiar"}
                          </button>
                        )}

                        <button
                          className="discount-action-btn shop"
                          onClick={() => navigate("/")}
                          disabled={isUsed}
                        >
                          <FaShoppingCart /> Usar
                        </button>
                      </div>

                      <div className="discount-date">
                        Conseguido:{" "}
                        {discount.claimed_at
                          ? new Date(discount.claimed_at).toLocaleDateString("es-ES")
                          : "Fecha no disponible"}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {activeTab === "orders" && (
          <div className="orders-tab-content">
            <div className="section-header">
              <h2>
                <FaBox /> Mis pedidos
              </h2>
              <p>Tus compras de packs Reanimeitor.</p>
            </div>

            {paymentVerifying && (
              <div className="settings-success-message">
                <FaSpinner className="spinning-icon" /> Verificando pago y preparando pedido...
              </div>
            )}

            {paymentNotice && (
              <div className="settings-success-message">
                <FaCheckCircle /> {paymentNotice}
              </div>
            )}

            {paymentError && (
              <div className="settings-error-message">
                <FaExclamationTriangle /> {paymentError}
              </div>
            )}

            {loadingOrders ? (
              <div className="loading-orders">
                <div className="spinner-large"></div>
                <p>Cargando pedidos...</p>
              </div>
            ) : ordersError ? (
              <div className="orders-error-message">
                <FaExclamationTriangle className="error-icon" />
                <h4>Error</h4>
                <p>{ordersError}</p>
                <button className="retry-btn" onClick={fetchMyOrders}>
                  Reintentar
                </button>
              </div>
            ) : myOrders.length === 0 ? (
              <div className="empty-orders">
                <FaBox className="empty-icon-large" />
                <h4>No tienes pedidos</h4>
                <p>Compra un pack y aparecerá aquí.</p>
                <button className="action-btn-empty" onClick={() => navigate("/")}>
                  <FaShoppingCart /> Ver packs
                </button>
              </div>
            ) : (
              <>
                {!isMobile && (
                  <div className="orders-table-container">
                    <table className="orders-table">
                      <thead>
                        <tr>
                          <th>Pedido</th>
                          <th>Pack</th>
                          <th>Total</th>
                          <th>Estado</th>
                          <th>Envío</th>
                          <th>Fecha</th>
                        </tr>
                      </thead>

                      <tbody>
                        {myOrders.map((order) => (
                          <tr
                            key={order.id}
                            className="order-row clickable"
                            onClick={() => navigate(`/order/${order.id}`)}
                          >
                            <td className="order-number">#{order.order_number}</td>
                            <td className="order-pack">
                              <span className="pack-name">{order.pack_name}</span>
                            </td>
                            <td className="order-price">
                              €{parseFloat(order.amount_eur).toFixed(2)}
                            </td>
                            <td>
                              <span className={`status-badge ${order.status}`}>
                                {order.status_label || order.status}
                              </span>
                            </td>
                            <td>
                              <span className={`shipping-badge ${order.shipping_status}`}>
                                {order.shipping_label || order.shipping_status}
                              </span>
                            </td>
                            <td className="order-date">
                              {order.fecha_formateada ||
                                new Date(order.created_at).toLocaleDateString("es-ES")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {isMobile && (
                  <div className="orders-mobile-list">
                    {myOrders.map((order) => (
                      <div
                        key={order.id}
                        className="order-card-mobile clickable"
                        onClick={() => navigate(`/order/${order.id}`)}
                      >
                        <div className="order-card-header">
                          <span className="order-number-mobile">#{order.order_number}</span>
                          <span className={`status-badge ${order.status}`}>
                            {order.status_label || order.status}
                          </span>
                        </div>

                        <div className="order-card-body">
                          <div className="order-pack-info">
                            <strong>{order.pack_name}</strong>
                          </div>

                          <div className="order-details-row">
                            <div className="order-detail">
                              <span className="detail-label">Total</span>
                              <span className="detail-value">
                                €{parseFloat(order.amount_eur).toFixed(2)}
                              </span>
                            </div>

                            <div className="order-detail">
                              <span className="detail-label">Envío</span>
                              <span className={`shipping-badge ${order.shipping_status}`}>
                                {order.shipping_label || order.shipping_status}
                              </span>
                            </div>

                            <div className="order-detail">
                              <span className="detail-label">Fecha</span>
                              <span className="detail-value">
                                {order.fecha_formateada ||
                                  new Date(order.created_at).toLocaleDateString("es-ES")}
                              </span>
                            </div>
                          </div>

                          {order.customer_address && (
                            <div className="order-address-mobile">
                              <small>
                                📍 {order.customer_address}, {order.customer_city}{" "}
                                {order.customer_postal_code}
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === "assistant" && (
          <section className="account-chat-panel account-glass-card">
            <div className="chat-panel-header">
              <div>
                <span className="chat-panel-kicker">
                  <FaRobot /> Reanimeitor IA
                </span>
                <h2>Asistente dentro de tu cuenta</h2>
                <p>
                  Puedes usar el chatbot sin salir de tu área privada. Si alguna función del chat no carga dentro del recuadro, ábrelo en pantalla completa.
                </p>
              </div>

              <a
                href={CHATBOT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="open-chat-btn"
              >
                <FaExternalLinkAlt /> Abrir completo
              </a>
            </div>

            <div className="chat-embed-shell">
              <iframe
                src={CHATBOT_URL}
                title="Reanimeitor IA"
                className="account-chat-iframe"
                loading="lazy"
              />
            </div>
          </section>
        )}
      </main>
    </div>
  );
}